import { createApiInstance } from './axios-config';
import {
  GraphNode,
  GraphEdge,
  GraphStats,
  // GraphQuery, // Unused
  GraphQueryResult,
  GraphScope,
  GraphExportOptions,
  GraphExportOptionsExtended,
  GraphFilterOptions,
  TemporalMetadata,
  GraphAnnotation,
} from '../../types/graph.types';

/**
 * Graph API Service
 * 
 * Provides methods for interacting with the knowledge graph visualization API.
 * All queries are automatically namespace-scoped for security.
 * 
 * Security Features:
 * - Automatic namespace enforcement
 * - Query sanitization
 * - Depth limiting
 * - Read-only access
 */
class GraphApiService {
  private readonly baseUrl = '/api/v1/graph';
  private readonly axios = createApiInstance();

  /**
   * Get nodes for the graph visualization
   * @param scope - 'personal' or 'organization'
   * @param filters - Optional filtering options
   */
  async getNodes(scope: GraphScope, filters?: GraphFilterOptions): Promise<{ data: GraphNode[] }> {
    const params = new URLSearchParams({
      scope,
      _t: Date.now().toString(), // Cache buster
      ...(filters?.minNodeDegree && { minDegree: filters.minNodeDegree.toString() }),
      ...(filters?.searchTerm && { search: filters.searchTerm }),
    });

    // Backend expects comma-separated string for nodeTypes
    if (filters?.showNodeTypes && filters.showNodeTypes.size > 0) {
      params.append('nodeTypes', Array.from(filters.showNodeTypes).join(','));
    }

    if (filters?.dateRange) {
      params.append('startDate', filters.dateRange.start.toISOString());
      params.append('endDate', filters.dateRange.end.toISOString());
    }

    const response = await this.axios.get(`${this.baseUrl}/nodes?${params}`);
    return response.data;
  }

  /**
   * Get edges for the graph visualization
   * @param scope - 'personal' or 'organization'
   * @param filters - Optional filtering options
   */
  async getEdges(scope: GraphScope, filters?: GraphFilterOptions): Promise<{ data: GraphEdge[] }> {
    const params = new URLSearchParams({
      scope,
      _t: Date.now().toString(), // Cache buster
      ...(filters?.searchTerm && { search: filters.searchTerm }),
    });

    // Backend expects comma-separated string for edgeTypes
    if (filters?.showEdgeTypes && filters.showEdgeTypes.size > 0) {
      params.append('edgeTypes', Array.from(filters.showEdgeTypes).join(','));
    }

    if (filters?.dateRange) {
      params.append('startDate', filters.dateRange.start.toISOString());
      params.append('endDate', filters.dateRange.end.toISOString());
    }

    const response = await this.axios.get(`${this.baseUrl}/edges?${params}`);
    return response.data;
  }

  /**
   * Get graph statistics
   * @param scope - 'personal' or 'organization'
   */
  async getStats(scope: GraphScope): Promise<{ data: GraphStats }> {
    const response = await this.axios.get(`${this.baseUrl}/stats`, {
      params: {
        scope,
        _t: Date.now().toString() // Cache buster
      },
    });
    return response.data;
  }

  /**
   * Execute a natural language query on the graph
   * @param query - Natural language query string
   * @param scope - 'personal' or 'organization'
   */
  async executeQuery(query: string, scope: GraphScope): Promise<{ data: GraphQueryResult }> {
    const response = await this.axios.post(`${this.baseUrl}/query`, {
      query,
      scope,
      // Security constraints are enforced server-side
    });
    return response.data;
  }

  /**
   * Get a specific node by ID
   * @param nodeId - The node ID
   * @param scope - 'personal' or 'organization'
   */
  async getNode(nodeId: string, scope: GraphScope): Promise<{ data: GraphNode }> {
    const response = await this.axios.get(`${this.baseUrl}/nodes/${nodeId}`, {
      params: { scope },
    });
    return response.data;
  }

  /**
   * Get related nodes for a specific node
   * @param nodeId - The node ID
   * @param scope - 'personal' or 'organization'
   * @param depth - How many levels of relationships to traverse (max 3)
   */
  async getRelatedNodes(
    nodeId: string, 
    scope: GraphScope, 
    depth: number = 1
  ): Promise<{ data: { nodes: GraphNode[]; edges: GraphEdge[] } }> {
    const response = await this.axios.get(`${this.baseUrl}/nodes/${nodeId}/related`, {
      params: { 
        scope, 
        depth: Math.min(depth, 3), // Max depth enforced
      },
    });
    return response.data;
  }

  /**
   * Search nodes by text
   * @param searchTerm - Text to search for
   * @param scope - 'personal' or 'organization'
   * @param limit - Maximum number of results
   */
  async searchNodes(
    searchTerm: string, 
    scope: GraphScope, 
    limit: number = 50
  ): Promise<{ data: GraphNode[] }> {
    const response = await this.axios.get(`${this.baseUrl}/search`, {
      params: { 
        q: searchTerm,
        scope,
        limit: Math.min(limit, 1000), // Max limit enforced
      },
    });
    return response.data;
  }

  /**
   * Export the graph in various formats
   * @param scope - 'personal' or 'organization'
   * @param options - Export options
   */
  async exportGraph(
    scope: GraphScope, 
    options: GraphExportOptions
  ): Promise<{ data: Blob | string }> {
    const response = await this.axios.post(
      `${this.baseUrl}/export`,
      { scope, ...options },
      {
        responseType: options.format === 'json' ? 'json' : 'blob',
      }
    );
    return response.data;
  }

  /**
   * Get suggested queries based on current context
   * @param scope - 'personal' or 'organization'
   * @param context - Optional context for suggestions
   */
  async getSuggestedQueries(
    scope: GraphScope,
    context?: string
  ): Promise<{ data: string[] }> {
    const response = await this.axios.get(`${this.baseUrl}/suggestions`, {
      params: { scope, context },
    });
    return response.data;
  }

  /**
   * Validate a natural language query before execution
   * @param query - Query to validate
   * @param scope - 'personal' or 'organization'
   */
  async validateQuery(
    query: string,
    scope: GraphScope
  ): Promise<{ data: { valid: boolean; message?: string } }> {
    const response = await this.axios.post(`${this.baseUrl}/validate-query`, {
      query,
      scope,
    });
    return response.data;
  }

  /**
   * Get the shortest path between two nodes
   * @param sourceId - Source node ID
   * @param targetId - Target node ID
   * @param scope - 'personal' or 'organization'
   */
  async getShortestPath(
    sourceId: string,
    targetId: string,
    scope: GraphScope
  ): Promise<{ data: { nodes: GraphNode[]; edges: GraphEdge[]; distance: number } }> {
    const response = await this.axios.get(`${this.baseUrl}/path`, {
      params: { source: sourceId, target: targetId, scope },
    });
    return response.data;
  }

  /**
   * Get synchronized nodes and edges for visualization
   * @param scope - 'personal' or 'organization'
   * @param filters - Optional filtering options
   */
  async getSynchronizedData(
    scope: GraphScope,
    filters?: GraphFilterOptions
  ): Promise<{ data: { nodes: GraphNode[]; edges: GraphEdge[] } }> {
    const params = new URLSearchParams({
      scope,
      _t: Date.now().toString(), // Cache buster
      ...(filters?.minNodeDegree && { minDegree: filters.minNodeDegree.toString() }),
      ...(filters?.searchTerm && { search: filters.searchTerm }),
    });

    // Backend expects comma-separated string for nodeTypes
    if (filters?.showNodeTypes && filters.showNodeTypes.size > 0) {
      const nodeTypesStr = Array.from(filters.showNodeTypes).join(',');
      params.append('nodeTypes', nodeTypesStr);
      console.log('📡 Sending nodeTypes to backend:', nodeTypesStr);
    }

    // Backend expects comma-separated string for edgeTypes
    if (filters?.showEdgeTypes && filters.showEdgeTypes.size > 0) {
      params.append('edgeTypes', Array.from(filters.showEdgeTypes).join(','));
    }

    const url = `${this.baseUrl}/sync?${params}`;
    console.log('📡 API Request URL:', url);
    const response = await this.axios.get(url);
    return response.data;
  }

  /**
   * Get centrality metrics for nodes
   * @param scope - 'personal' or 'organization'
   * @param metric - Type of centrality metric
   */
  async getCentrality(
    scope: GraphScope,
    metric: 'degree' | 'betweenness' | 'closeness' | 'eigenvector' = 'degree'
  ): Promise<{ data: Array<{ nodeId: string; label: string; score: number }> }> {
    const response = await this.axios.get(`${this.baseUrl}/centrality`, {
      params: { scope, metric },
    });
    return response.data;
  }

  /**
   * Get clustering coefficient for the graph
   * @param scope - 'personal' or 'organization'
   */
  async getClusteringCoefficient(
    scope: GraphScope
  ): Promise<{ data: { global: number; local: Record<string, number> } }> {
    const response = await this.axios.get(`${this.baseUrl}/clustering`, {
      params: { scope },
    });
    return response.data;
  }

  /**
   * Get community detection results
   * @param scope - 'personal' or 'organization'
   * @param algorithm - Detection algorithm to use
   */
  async detectCommunities(
    scope: GraphScope,
    algorithm: 'louvain' | 'label-propagation' = 'louvain'
  ): Promise<{ data: Array<{ communityId: string; nodes: string[] }> }> {
    const response = await this.axios.get(`${this.baseUrl}/communities`, {
      params: { scope, algorithm },
    });
    return response.data;
  }

  /**
   * Get trending topics in the graph
   * @param scope - 'personal' or 'organization'
   * @param timeRange - Time range for trending analysis
   */
  async getTrendingTopics(
    scope: GraphScope,
    timeRange: '1d' | '7d' | '30d' = '7d'
  ): Promise<{ data: Array<{ topic: string; count: number; trend: number }> }> {
    const response = await this.axios.get(`${this.baseUrl}/trending`, {
      params: { scope, range: timeRange },
    });
    return response.data;
  }

  // ===== Week 7: Temporal Graph Views =====

  /**
   * Get temporal graph data with date range filtering and distribution
   * @param scope - 'personal' or 'organization'
   * @param startDate - Start of date range (ISO 8601)
   * @param endDate - End of date range (ISO 8601)
   * @param granularity - Bucket size: 'day', 'week', or 'month'
   */
  async getTemporalData(
    scope: GraphScope,
    startDate: string,
    endDate: string,
    granularity: 'day' | 'week' | 'month' = 'day'
  ): Promise<{ data: { nodes: GraphNode[]; edges: GraphEdge[]; temporalMetadata: TemporalMetadata } }> {
    const params = new URLSearchParams({
      scope,
      startDate,
      endDate,
      granularity,
      includeEdges: 'true',
    });

    const response = await this.axios.get(`${this.baseUrl}/temporal?${params}`);
    return response.data;
  }

  // ===== Week 8: Graph Annotations =====

  /**
   * Create an annotation on a graph node or edge
   */
  async createAnnotation(
    nodeId: string | undefined,
    edgeId: string | undefined,
    scope: GraphScope,
    content: string,
    parentAnnotationId?: string,
    mentions?: string[]
  ): Promise<{ data: GraphAnnotation }> {
    const response = await this.axios.post(`${this.baseUrl}/annotations`, {
      nodeId,
      edgeId,
      scope,
      content,
      parentAnnotationId,
      mentions,
    });
    return response.data;
  }

  /**
   * Get annotations for a node or edge
   */
  async getAnnotations(
    nodeId: string | undefined,
    edgeId: string | undefined,
    scope: GraphScope
  ): Promise<{ data: GraphAnnotation[] }> {
    const params = new URLSearchParams({ scope });
    if (nodeId) params.append('nodeId', nodeId);
    if (edgeId) params.append('edgeId', edgeId);

    const response = await this.axios.get(`${this.baseUrl}/annotations?${params}`);
    return response.data;
  }

  /**
   * Get annotation counts for multiple nodes (bulk fetch for visual indicators)
   */
  async getAnnotationCounts(
    nodeIds: string[],
    scope: GraphScope
  ): Promise<{ data: Record<string, number> }> {
    const params = new URLSearchParams({
      scope,
      nodeIds: nodeIds.join(','),
    });

    const response = await this.axios.get(`${this.baseUrl}/annotations/bulk?${params}`);
    return response.data;
  }

  /**
   * Update an annotation's content
   */
  async updateAnnotation(
    annotationId: string,
    content: string
  ): Promise<{ data: GraphAnnotation }> {
    const response = await this.axios.put(`${this.baseUrl}/annotations/${annotationId}`, {
      content,
    });
    return response.data;
  }

  /**
   * Resolve an annotation
   */
  async resolveAnnotation(
    annotationId: string
  ): Promise<{ data: GraphAnnotation }> {
    const response = await this.axios.post(`${this.baseUrl}/annotations/${annotationId}/resolve`);
    return response.data;
  }

  /**
   * Delete an annotation
   */
  async deleteAnnotation(
    annotationId: string
  ): Promise<void> {
    await this.axios.delete(`${this.baseUrl}/annotations/${annotationId}`);
  }

  // ===== Week 12: Semantic Relationships =====

  /**
   * Get semantic relationships for a specific node
   * @param nodeUid - The node UID
   * @param scope - 'personal' or 'organization'
   */
  async getNodeRelationships(
    nodeUid: string,
    scope: GraphScope
  ): Promise<{ data: any[] }> {
    const response = await this.axios.get(`/api/v1/relationships/node/${nodeUid}`, {
      params: { scope },
    });
    return response.data;
  }

  /**
   * Get semantic relationships for multiple nodes (batch)
   * @param nodeUids - Array of node UIDs
   * @param scope - 'personal' or 'organization'
   */
  async getBatchRelationships(
    nodeUids: string[],
    scope: GraphScope
  ): Promise<{ data: any[] }> {
    const response = await this.axios.get(`/api/v1/relationships/batch`, {
      params: {
        nodeUids: nodeUids.join(','),
        scope,
      },
    });
    return response.data;
  }

  // ===== Week 8: Advanced Export =====

  /**
   * Export graph with extended options (filters, annotations, statistics)
   */
  async exportGraphExtended(
    scope: GraphScope,
    options: GraphExportOptionsExtended
  ): Promise<Blob | string> {
    const response = await this.axios.post(
      `${this.baseUrl}/export`,
      { scope, ...options },
      {
        responseType: options.format === 'json' ? 'json' : 'blob',
      }
    );
    return response.data;
  }
}

// Export singleton instance
export const graphApi = new GraphApiService();

// Export class for testing
export default GraphApiService;